import { Link } from "react-router-dom";
import type { Member } from "@/lib/types";
import Avatar from "@/components/base/Avatar";
import { contrastText, validColor } from "@/lib/color";

interface MemberCardProps {
  member: Member;
  colorMap: Record<string, string>;
}

function cateLabel(m: Member): string {
  return String(m.cate_name || m.cate || m.category || "").trim();
}

function bizName(m: Member): string {
  return String(m.biz_name || m.company || "").trim();
}

export default function MemberCard({ member, colorMap }: MemberCardProps) {
  const cate = cateLabel(member);
  const color =
    validColor(String(member.color ?? "")) || validColor(colorMap[cate]) || "#17408B";
  const biz = bizName(member);
  const subtitle = [biz, member.position].filter(Boolean).join(" · ");

  return (
    <Link
      to={`/members/${member.no}`}
      className="relative flex flex-col rounded-xl border border-background-200 bg-background-50 p-5 transition-colors hover:border-primary-200 hover:bg-primary-50/40"
    >
      <div className="flex items-start gap-3.5">
        <Avatar name={member.name} photo={member.photo} color={color} size={56} />
        <div className="min-w-0 flex-1">
          <h3 className="font-heading text-base font-bold text-foreground-950">
            {member.name}
            {member.aho && (
              <span className="ml-1.5 text-sm font-medium text-foreground-500">
                ({member.aho})
              </span>
            )}
          </h3>
          {cate && (
            <span
              className="mt-1.5 inline-block rounded-full px-2.5 py-0.5 text-xs font-semibold"
              style={{ backgroundColor: color, color: contrastText(color) }}
            >
              {cate}
            </span>
          )}
        </div>
      </div>

      {subtitle && (
        <p className="mt-3 truncate text-sm font-medium text-foreground-800">{subtitle}</p>
      )}

      {member.address && (
        <p className="mt-1 flex items-start gap-1 text-xs text-foreground-500">
          <i className="ri-map-pin-line mt-0.5 shrink-0" aria-hidden="true"></i>
          <span className="line-clamp-1">{member.address}</span>
        </p>
      )}

      {member.has_map === 1 && (
        <span
          aria-label="비즈맵 등록 회원"
          className="absolute bottom-4 right-4 w-7 h-7 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-800"
        >
          <i className="ri-map-pin-2-line text-sm" aria-hidden="true"></i>
        </span>
      )}
    </Link>
  );
}